import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Drawer, InputNumber, Select, Typography } from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  BarChartOutlined,
  TagOutlined,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import type {
  ProviderService,
  ServiceType,
} from '../../../types/provider';
import type { LanguageCode } from '../../../types/i18n';
import { pickTranslatedName } from '../../../utils/i18nDisplay';

const { Text } = Typography;

export type ServiceModalMode = 'create' | 'edit';

type Errors = { serviceType?: boolean; price?: boolean };

interface ServiceModalProps {
  open: boolean;
  mode: ServiceModalMode;
  editService: Partial<ProviderService> | null;
  saving: boolean;
  serviceTypes: ServiceType[];
  usedServiceTypeIds: Set<number | null>;
  slotStep: number;
  currentLanguage: LanguageCode;
  defaultLanguage: LanguageCode;
  onChange: (next: Partial<ProviderService>) => void;
  onCancel: () => void;
  onSave: (payload: Partial<ProviderService>) => void;
}

/**
 * Xizmatni qo'shish / tahrirlash bottom sheet — gradient hero, polished
 * form qatorlari, BookingDetailSheet uslubidagi dizayn tili.
 */
export function ServiceModal({
  open,
  mode,
  editService,
  saving,
  serviceTypes,
  usedServiceTypeIds,
  slotStep,
  currentLanguage,
  defaultLanguage,
  onChange,
  onCancel,
  onSave,
}: ServiceModalProps) {
  const { t } = useTranslation(['common', 'provider']);
  const [errors, setErrors] = useState<Errors>({});

  // Close animation davomida content saqlanib turishi uchun internal state
  const [internalService, setInternalService] =
    useState<Partial<ProviderService> | null>(editService);
  const [internalMode, setInternalMode] = useState<ServiceModalMode>(mode);

  useEffect(() => {
    if (editService) {
      setInternalService(editService);
      setInternalMode(mode);
    }
  }, [editService, mode]);

  const handleAfterOpenChange = (visible: boolean) => {
    if (!visible) {
      setInternalService(null);
      setErrors({});
    }
  };

  // Telegram BackButton
  useEffect(() => {
    if (!open) return;
    const handler = () => !saving && onCancel();
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(handler);
    return () => {
      WebApp.BackButton.offClick(handler);
      WebApp.BackButton.hide();
    };
  }, [open, onCancel, saving]);

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  const bookingModeLabel = (m: string) =>
    t(`provider:profile.bookingMode.${m}`, { defaultValue: m });

  const availableServiceTypes = serviceTypes.filter(
    (tp) => tp.active && !usedServiceTypeIds.has(tp.id),
  );

  const resolveDisplayName = (
    svc: Partial<ProviderService> | null,
  ): string => {
    if (!svc) return '';
    if (svc.serviceTypeId != null) {
      const parent = serviceTypes.find((tp) => tp.id === svc.serviceTypeId);
      if (parent) {
        return (
          pickTranslatedName(parent, currentLanguage, defaultLanguage) ||
          svc.name ||
          ''
        );
      }
    }
    return svc.name ?? '';
  };

  const validateAndSave = () => {
    if (!editService) return;
    const next: Errors = {};
    if (mode === 'create' && !editService.serviceTypeId) {
      next.serviceType = true;
    }
    if (editService.price == null) next.price = true;
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    onSave(editService);
  };

  if (!internalService) return null;

  const isCreate = internalMode === 'create';
  const displayedName = resolveDisplayName(internalService);

  // Heron accent — create=accent ko'k, edit=mor (xizmat o'zgartirishni ko'rsatadi)
  const heroAccent = isCreate ? '#3390ec' : '#9254de';
  const heroAccentDark = isCreate ? '#1677ff' : '#722ed1';

  return (
    <Drawer
      open={open}
      placement="bottom"
      onClose={onCancel}
      closable={false}
      maskClosable={!saving}
      height="auto"
      afterOpenChange={handleAfterOpenChange}
      styles={{
        body: {
          padding: 0,
          background: 'var(--bl-surface-soft, #f7f8fa)',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
        },
        wrapper: {
          borderRadius: '20px 20px 0 0',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 32px)',
        },
        mask: { background: 'rgba(15,23,42,0.45)' },
      }}
    >
      {/* Drag handle */}
      <div
        style={{
          flexShrink: 0,
          padding: '10px 0 8px',
          background: 'var(--bl-surface, #fff)',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--bl-divider, #e5e7eb)',
            margin: '0 auto',
          }}
        />
      </div>

      {/* Hero */}
      <div
        style={{
          flexShrink: 0,
          position: 'relative',
          padding: '12px 18px 16px',
          background: `linear-gradient(135deg, ${heroAccent}10 0%, ${heroAccent}1c 60%, ${heroAccent}08 100%)`,
          borderBottom: `1px solid ${heroAccent}14`,
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -40,
            right: -30,
            width: 130,
            height: 130,
            borderRadius: '50%',
            background: `${heroAccent}1f`,
            filter: 'blur(6px)',
            opacity: 0.55,
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${heroAccent} 0%, ${heroAccentDark} 100%)`,
              color: '#fff',
              fontSize: 19,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: `0 4px 12px ${heroAccent}38, inset 0 1px 0 rgba(255,255,255,0.22)`,
            }}
          >
            {isCreate ? <PlusOutlined /> : <EditOutlined />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--bl-text-muted, #6b7280)',
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 2,
              }}
            >
              {isCreate
                ? t('provider:profile.services.addModalTitle')
                : t('provider:profile.services.editModalTitle')}
            </Text>
            <Text
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.3,
                lineHeight: 1.2,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {!isCreate && displayedName
                ? displayedName
                : t('provider:profile.services.title')}
            </Text>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            aria-label={t('common:actions.cancel')}
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.85)',
              color: 'var(--bl-text-muted, #6b7280)',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              WebkitTapHighlightColor: 'transparent',
              flexShrink: 0,
            }}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '14px 16px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {/* Xizmat turi */}
        {isCreate ? (
          <FieldRow
            icon={<TagOutlined />}
            iconColor="#3390ec"
            label={t('provider:profile.services.serviceTypeLabel')}
            error={errors.serviceType}
            errorText={t('provider:profile.services.serviceTypeRequired')}
          >
            <Select
              value={editService?.serviceTypeId}
              onChange={(v) => {
                if (!editService) return;
                onChange({ ...editService, serviceTypeId: v });
                setErrors((e) => ({ ...e, serviceType: false }));
              }}
              style={{ width: '100%' }}
              size="large"
              status={errors.serviceType ? 'error' : undefined}
              placeholder={t('provider:profile.services.serviceTypePlaceholder')}
              options={availableServiceTypes.map((tp) => ({
                value: tp.id,
                label:
                  pickTranslatedName(tp, currentLanguage, defaultLanguage) ||
                  tp.name,
              }))}
            />
          </FieldRow>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              background:
                'linear-gradient(135deg, rgba(31,139,76,0.06) 0%, rgba(31,139,76,0.14) 100%)',
              border: '1px solid rgba(31,139,76,0.18)',
              borderRadius: 14,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 11,
                background:
                  'linear-gradient(135deg, rgba(31,139,76,0.16) 0%, rgba(31,139,76,0.30) 100%)',
                border: '1px solid rgba(31,139,76,0.18)',
                color: '#1f8b4c',
                fontSize: 15,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CheckOutlined />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#1f8b4c',
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 1,
                }}
              >
                {t('provider:profile.services.serviceTypeLabel')}
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--bl-text-strong, #0f172a)',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayedName}
              </Text>
            </div>
          </div>
        )}

        {/* Davomiyligi */}
        <FieldRow
          icon={<ClockCircleOutlined />}
          iconColor="#9254de"
          label={t('provider:profile.services.durationLabel')}
        >
          <Select
            value={editService?.durationMinutes}
            onChange={(v) => {
              if (!editService) return;
              onChange({ ...editService, durationMinutes: v });
            }}
            style={{ width: '100%' }}
            size="large"
            options={Array.from(
              { length: Math.floor(480 / slotStep) },
              (_, i) => {
                const val = (i + 1) * slotStep;
                return {
                  value: val,
                  label: t('common:units.minutes', { count: val }),
                };
              },
            )}
          />
        </FieldRow>

        {/* Narx */}
        <FieldRow
          icon={
            <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
              {t('provider:common.currency')}
            </span>
          }
          iconColor="#d97706"
          label={t('provider:profile.services.priceLabel')}
          error={errors.price}
          errorText={t('provider:profile.services.priceRequired')}
        >
          <InputNumber
            value={editService?.price ?? null}
            onChange={(v) => {
              if (!editService) return;
              onChange({ ...editService, price: v ?? undefined });
              setErrors((e) => ({ ...e, price: false }));
            }}
            min={0}
            step={1000}
            size="large"
            style={{ width: '100%', borderRadius: 10 }}
            status={errors.price ? 'error' : undefined}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            inputMode="numeric"
            placeholder="0"
            addonAfter={t('provider:common.currency')}
          />
        </FieldRow>

        {/* Bron rejimi */}
        <FieldRow
          icon={<BarChartOutlined />}
          iconColor="#3390ec"
          label={t('provider:profile.services.bookingModeLabel')}
        >
          <Select
            value={editService?.bookingMode}
            onChange={(v) => {
              if (!editService) return;
              onChange({ ...editService, bookingMode: v });
            }}
            style={{ width: '100%' }}
            size="large"
            options={['MANUAL_CONFIRM', 'FREE_AUTO_CONFIRM'].map((k) => ({
              value: k,
              label: bookingModeLabel(k),
            }))}
          />
        </FieldRow>
      </div>

      {/* Footer actions */}
      <div
        style={{
          flexShrink: 0,
          padding: '12px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
          background: 'var(--bl-surface, #fff)',
          borderTop: '1px solid var(--bl-divider, #ebebeb)',
          display: 'flex',
          gap: 8,
        }}
      >
        <SecondaryButton
          label={t('common:actions.cancel')}
          disabled={saving}
          onClick={onCancel}
        />
        <PrimaryButton
          icon={isCreate ? <PlusOutlined /> : <SaveOutlined />}
          label={
            isCreate
              ? t('provider:profile.services.addAction')
              : t('common:actions.save')
          }
          loading={saving}
          onClick={validateAndSave}
        />
      </div>
    </Drawer>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

interface FieldRowProps {
  icon: ReactNode;
  iconColor: string;
  label: string;
  required?: boolean;
  error?: boolean;
  errorText?: string;
  hint?: string;
  children: ReactNode;
}

function FieldRow({
  icon,
  iconColor,
  label,
  required,
  error,
  errorText,
  hint,
  children,
}: FieldRowProps) {
  return (
    <div
      style={{
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 14,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${iconColor}14 0%, ${iconColor}26 100%)`,
            border: `1px solid ${iconColor}1a`,
            color: iconColor,
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <Text
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: error ? '#d93b3e' : 'var(--bl-text-muted, #6b7280)',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#d93b3e', marginLeft: 3 }}>*</span>
          )}
        </Text>
      </div>
      {children}
      {error && errorText && (
        <Text
          style={{
            fontSize: 11,
            color: '#d93b3e',
            display: 'block',
            fontWeight: 500,
          }}
        >
          {errorText}
        </Text>
      )}
      {!error && hint && (
        <Text
          style={{
            fontSize: 11,
            color: 'var(--bl-text-muted, #9ca3af)',
            display: 'block',
            lineHeight: 1.4,
          }}
        >
          {hint}
        </Text>
      )}
    </div>
  );
}

function PrimaryButton({
  icon,
  label,
  loading,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  loading: boolean;
  onClick: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={() => !loading && onClick()}
      disabled={loading}
      onTouchStart={() => !loading && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !loading && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        flex: 2,
        minHeight: 48,
        padding: '0 16px',
        border: '1px solid rgba(22,119,255,0.18)',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.85 : 1,
        boxShadow: '0 4px 14px rgba(22,119,255,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
        transform: pressed && !loading ? 'scale(0.985)' : 'scale(1)',
        transition: 'transform 0.1s ease, opacity 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        outline: 'none',
        letterSpacing: 0.15,
        fontFamily: 'inherit',
      }}
    >
      {loading ? <LoadingOutlined spin /> : icon}
      <span>{label}</span>
    </button>
  );
}

function SecondaryButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      onTouchStart={() => !disabled && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        flex: 1,
        minHeight: 48,
        padding: '0 16px',
        border: 'none',
        borderRadius: 12,
        background: pressed
          ? 'var(--bl-surface-soft, #ebedf0)'
          : 'var(--bl-surface-soft, #f7f8fa)',
        color: 'var(--bl-text-muted, #6b7280)',
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transform: pressed && !disabled ? 'scale(0.985)' : 'scale(1)',
        transition: 'background 0.15s ease, transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
        opacity: disabled ? 0.55 : 1,
        outline: 'none',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}
